// RegistrationSection.jsx
import React, { useState } from 'react';

const RegistrationSection = ({
  applicationData,
  mode,
  applicationId,
  checkMR,
  setMR,
  downloadFile,
  onChange,
  userRole
}) => {
  const [loading,  setLoading]  = useState(false);
  const [message,  setMessage]  = useState(null);  // { type: 'success'|'danger'|'warning', text }

  const childData = {
    firstName:             applicationData.childFirstName,
    lastName:              applicationData.childLastName,
    birthDate:             applicationData.birthDate,
    birthCertificateNumber: applicationData.birthCertificate,
  };

  const isMRConfirmed = applicationData.registrationStatus === 'verified';

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 6000);
  };

  // --- Перевірити через реєстр ---
  const handleCheck = async () => {
    if (!childData.lastName) {
      showMsg('warning', 'В заявці відсутнє прізвище дитини. Неможливо перевірити реєстрацію.');
      return;
    }
    try {
      setLoading(true);
      const result = await checkMR(childData);

      if (result?.status === 'ok') {
        showMsg('success', 'Інформація підтверджена, дитина зареєстрована.');
        await onChange?.();
        return;
      }
      if (result?.Error?.status === 'Not Found') {
        showMsg('danger', 'Є відмінності у написанні імені або дати народження в заявці та реєстрі. Перевірте — має збігатися зі свідоцтвом про народження.');
        return;
      }
      if (typeof result?.Error === 'string' && result.Error.match(/not found bcert only/)) {
        showMsg('danger', 'Не знайдено такого свідоцтва в реєстрі. Зверніться до ЦНАП. Реєстрація не підтверджена.');
        return;
      }
      showMsg('warning', 'Отримано відповідь від реєстру без чіткого статусу. Перевірте дані вручну.');
    } catch (err) {
      showMsg('danger', 'Помилка запиту до реєстру: ' + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  // --- Встановити вручну ---
  const handleSetMR = async () => {
    try {
      setLoading(true);
      await setMR(childData, true);
      await onChange?.();
      showMsg('success', 'Реєстрацію підтверджено вручну.');
    } catch (err) {
      showMsg('danger', 'Помилка підтвердження: ' + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  // --- Скасувати вручну ---
  const handleDecMR = async () => {
    try {
      setLoading(true);
      await setMR(childData, false);
      await onChange?.();
      showMsg('success', 'Реєстрацію скасовано.');
    } catch (err) {
      showMsg('danger', 'Помилка скасування: ' + (err?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc, part) => {
    try {
      await downloadFile(applicationData.id, doc.id, part, doc.originalName);
    } catch (err) {
      console.error('Error downloading document:', err);
    }
  };

  const getDocumentIcon = (type) => {
    const icons = { pdf: '📄', jpg: '🖼️', png: '🖼️', zip: '📦', docx: '📝', xlsx: '📊' };
    return icons[type] || '📎';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try { return new Date(dateString).toLocaleDateString('uk-UA'); } catch { return dateString; }
  };

  return (
    <div className="registration-section">
      <h3 className="section-title">Місце реєстрації</h3>

      {/* Повідомлення */}
      {message && (
        <div className={`alert alert-${message.type} py-2 px-3 mb-3`}>
          {message.text}
        </div>
      )}

      {/* Дані дитини */}
      <div className="data-section">
        <div className="data-row">
          <label className="data-label">Зареєстрована адреса:</label>
          <span className="data-value">{applicationData.registeredAddress || '—'}</span>
        </div>
        <div className="data-row">
          <label className="data-label">Фактична адреса:</label>
          <span className="data-value">{applicationData.actualAddress || '—'}</span>
        </div>
        <div className="data-row">
          <label className="data-label">Свідоцтво про народження:</label>
          <span className="data-value">{applicationData.birthCertificate || '—'}</span>
        </div>
      </div>

      {/* Статус реєстрації в РТГК */}
      <div className="data-section mt-3">
        <div className="data-row align-items-center">
          <label className="data-label">Статус реєстрації (РТГК):</label>
          <span className="data-value">
            {isMRConfirmed
              ? <span className="status-badge status-verified">✓ Реєстрація підтверджена</span>
              : <span className="status-badge status-pending">Немає підтвердженої інформації</span>
            }
          </span>
        </div>

        {/* Хто і коли підтвердив */}
        {isMRConfirmed && applicationData.statusMR?.date && (
          <div className="data-row">
            <label className="data-label">Дата підтвердження:</label>
            <span className="data-value">{formatDate(applicationData.statusMR.date)}</span>
          </div>
        )}
      </div>

      {/* Документи */}
      <div className="documents-section mt-3">
        <h4 className="documents-title">Документи для реєстрації</h4>
        <div className="documents-list">
          {applicationData.registrationDocuments?.map(doc => (
            <div key={doc.id} className="document-item">
              <div className="document-info">
                <span className="document-icon">{getDocumentIcon(doc.type)}</span>
                <div className="document-details">
                  <div className="document-name">{doc.name}</div>
                  <div className="document-meta">
                    <span className="document-type">{doc.type}</span>
                    <span className="document-size">{doc.size}</span>
                    <span className="document-date">{doc.uploadedAt}</span>
                  </div>
                </div>
              </div>
              <button
                className="btn-download"
                onClick={() => handleDownload(doc, 'cert')}
                title="Завантажити документ"
                disabled={loading}
              >
                ⬇️
              </button>
            </div>
          ))}
          {(!applicationData.registrationDocuments || applicationData.registrationDocuments.length === 0) && (
            <div className="no-documents">Документи відсутні</div>
          )}
        </div>
      </div>

      {/* Дії модератора */}
      {mode === 'edit' && (
        <div className="actions-section mt-3">
          <div className="action-buttons d-flex gap-2 flex-wrap">

            {/* Перевірити через реєстр */}
            <button
              className="btn btn-outline-primary"
              onClick={handleCheck}
              disabled={loading}
              title="Автоматична перевірка через реєстр територіальної громади"
            >
              {loading ? 'Обробка...' : '🔍 Перевірити через реєстр'}
            </button>

            {/* Встановити вручну */}
            <button
              className="btn btn-success"
              onClick={handleSetMR}
              disabled={loading || isMRConfirmed || !["admin"].includes(userRole)}
              title="Встановити реєстрацію вручну"
            >
              ✓ Встановити вручну
            </button>

            {/* Скасувати */}
            <button
              className="btn btn-danger"
              onClick={handleDecMR}
              disabled={loading || !isMRConfirmed || !["admin"].includes(userRole)}
              title="Скасувати підтвердження реєстрації"
            >
              ✕ Скасувати реєстрацію
            </button>
          </div>
          <div className="mt-2" style={{ fontSize: '0.85em', color: '#666' }}>
            <em>Встановити / Скасувати — ручний режим без перевірки через реєстр</em>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationSection;